/**
 * SiteBuilder.js
 *
 * El editor completo. Barra superior, biblioteca a la izquierda, lienzo al centro,
 * panel de propiedades a la derecha.
 *
 * Todo el estado del contenido vive en un solo historial (`hist`), así que
 * deshacer/rehacer funciona sobre cualquier cambio — texto, estilo, mover, borrar,
 * importar una sección — sin que cada acción tenga que acordarse de registrarse.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Canvas from './Canvas';
import StylePanel from './StylePanel';
import {
  findNode, findPath, updateProps, updateStyle, updateAdvanced, removeNode, duplicateNode,
  insertNode, moveNode, reorderSibling, createHistory, pushHistory, undo, redo,
  themeToCssVars, ensureFontsLoaded, DEFAULT_THEME, countNodes, downloadJson, readJsonFile,
  deepClone
} from './builderModel';
import {
  ELEMENT_LIBRARY, ELEMENT_GROUPS, SECTION_PRESETS, SECTION_GROUPS,
  makeElement, makeSection, makeRow, LAYOUT_PRESETS
} from './blockLibrary';
import API_URL from '../../../config/api';
import './builder.css';

const AUTOSAVE_MS = 1500;

function authHeaders() {
  const token = sessionStorage.getItem('token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
    : { 'Content-Type': 'application/json' };
}

async function api(path, options) {
  const r = await fetch(API_URL + path, Object.assign({ headers: authHeaders() }, options));
  const text = await r.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }
  if (!r.ok) throw new Error((json && json.error) || 'Error ' + r.status);
  return json;
}

function approvalNotice(result, fallback) {
  if (!result || !result.approval_required) return false;
  return result.message || fallback || 'Tu cambio fue enviado a aprobación de MARKETOS. Todavía no se ha guardado ni publicado.';
}

export default function SiteBuilder({ siteId, onExit }) {
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [settings, setSettings] = useState({});
  const [hist, setHist] = useState(() => createHistory([]));
  const [selectedId, setSelectedId] = useState(null);
  const [breakpoint, setBreakpoint] = useState('desktop');
  const [leftTab, setLeftTab] = useState('sections');
  const [status, setStatus] = useState({ text: '', kind: '' });
  const [saving, setSaving] = useState('idle'); // idle | saving | saved | pending | error
  const [loading, setLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showSeo, setShowSeo] = useState(false);

  const drag = useRef(null);
  const saveTimer = useRef(null);
  const dirty = useRef(false);
  const importInput = useRef(null);

  const tree = hist.present;
  const page = pages.find((p) => p.id === pageId) || null;
  const selected = selectedId ? findNode(tree, selectedId) : null;
  const cssVars = useMemo(() => themeToCssVars(theme), [theme]);

  const notify = (text, kind) => {
    setStatus({ text, kind: kind || 'info' });
    window.clearTimeout(notify._t);
    notify._t = window.setTimeout(() => setStatus({ text: '', kind: '' }), 4000);
  };

  /* ------------------------- carga ------------------------- */

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api('/api/pos/sites/' + siteId);
        if (!alive) return;
        setSite({ id: data.id, name: data.name, slug: data.slug, status: data.status });
        setTheme(data.theme || DEFAULT_THEME);
        setSettings(data.settings || {});
        setPages(data.pages || []);
        const home = (data.pages || []).find((p) => p.is_home) || (data.pages || [])[0];
        if (home) {
          setPageId(home.id);
          setHist(createHistory(home.content || []));
        }
      } catch (e) {
        notify(e.message, 'error');
      } finally {
        if (alive) setLoading(false);
      }
      // Estos dos son opcionales: si el usuario no tiene permiso, el editor debe
      // seguir funcionando igual, solo sin esas listas.
      api('/api/pos/site-templates').then((t) => alive && setTemplates(t || [])).catch(() => {});
      api('/api/pos/crm/calendars').then((c) => alive && setCalendars(c || [])).catch(() => {});
    })();
    return () => { alive = false; };
  }, [siteId]);

  useEffect(() => { ensureFontsLoaded(theme); }, [theme]);

  /* ------------------------- guardado ------------------------- */

  const savePage = useCallback(async () => {
    if (!pageId) return false;
    setSaving('saving');
    try {
      const result = await api('/api/pos/sites/' + siteId + '/pages/' + pageId, {
        method: 'PUT',
        body: JSON.stringify({ content: hist.present })
      });
      const pending = approvalNotice(result);
      if (pending) {
        // 202 significa que la solicitud quedó pendiente de aprobación; no es
        // un guardado efectivo. Conservamos dirty para no perder el trabajo.
        setSaving('pending');
        notify(pending, 'info');
        return false;
      }
      dirty.current = false;
      setSaving('saved');
      setPages((prev) => prev.map((p) => (p.id === pageId
        ? Object.assign({}, p, { content: hist.present, hasUnpublishedChanges: true })
        : p)));
      return true;
    } catch (e) {
      setSaving('error');
      // El mensaje del backend es específico ("«form» no puede ir dentro de «row»"),
      // así que se muestra tal cual en vez de un genérico inútil.
      notify('No se guardó: ' + e.message, 'error');
      return false;
    }
  }, [pageId, siteId, hist.present]);

  useEffect(() => {
    if (!dirty.current) return undefined;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(savePage, AUTOSAVE_MS);
    return () => window.clearTimeout(saveTimer.current);
  }, [hist.present, savePage]);

  // Cerrar la pestaña con cambios sin guardar es la forma más tonta de perder
  // media hora de trabajo.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const commit = useCallback((nextTree, coalesce) => {
    dirty.current = true;
    setSaving('idle');
    setHist((h) => pushHistory(h, nextTree, coalesce));
  }, []);

  /* ------------------------- acciones sobre el árbol ------------------------- */

  const actions = useMemo(() => ({
    setProps: (id, patch) => commit(updateProps(tree, id, patch), true),
    setStyle: (id, bp, key, value) => commit(updateStyle(tree, id, bp, key, value), true),
    setAdvanced: (id, patch) => commit(updateAdvanced(tree, id, patch)),
    remove: (id) => {
      commit(removeNode(tree, id));
      if (selectedId === id) setSelectedId(null);
    },
    duplicate: (id) => commit(duplicateNode(tree, id)),
    move: (id, delta) => commit(reorderSibling(tree, id, delta)),
    addBlankSection: () => {
      const section = makeSection();
      commit(tree.concat([section]));
      setSelectedId(section.id);
    },
    saveTemplate: async (id) => {
      const node = findNode(tree, id);
      if (!node) return;
      const name = window.prompt('Nombre para esta plantilla de sección:', 'Mi sección');
      if (!name) return;
      try {
        await api('/api/pos/site-templates', {
          method: 'POST',
          body: JSON.stringify({
            name, scope: 'section',
            payload: { format: 'marketos.export', version: 1, kind: 'section', payload: { node: deepClone(node), theme } }
          })
        });
        const t = await api('/api/pos/site-templates');
        setTemplates(t || []);
        notify('Plantilla guardada. Ya la puedes usar en cualquier sitio de esta cuenta.', 'ok');
      } catch (e) {
        notify(e.message, 'error');
      }
    }
  }), [tree, commit, selectedId, theme]);

  /* ------------------------- drag & drop ------------------------- */

  const setDragState = useCallback((payload) => { drag.current = payload; }, []);

  const handleDrop = useCallback((targetId, position) => {
    const payload = drag.current;
    drag.current = null;
    if (!payload) return;

    if (payload.kind === 'move') {
      if (position === 'end') return;
      commit(moveNode(tree, payload.id, targetId, position));
      return;
    }

    const node = payload.make();
    if (!node) return;
    const next = position === 'end' || !targetId
      ? tree.concat([node])
      : insertNode(tree, targetId, position, node);
    commit(next);
    setSelectedId(node.id);
  }, [tree, commit]);

  const startDragNew = (nodeType, make) => (e) => {
    setDragState({ kind: 'new', nodeType, make });
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', nodeType);
  };

  /* ------------------------- atajos de teclado ------------------------- */

  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      const typing = target && (target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        if (typing) return;
        e.preventDefault(); setHist((h) => undo(h)); dirty.current = true;
      } else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        if (typing) return;
        e.preventDefault(); setHist((h) => redo(h)); dirty.current = true;
      } else if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault(); savePage();
      } else if (mod && e.key.toLowerCase() === 'd' && selectedId && !typing) {
        e.preventDefault(); actions.duplicate(selectedId);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !typing) {
        e.preventDefault(); actions.remove(selectedId);
      } else if (e.key === 'Escape') {
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, actions, savePage]);

  /* ------------------------- páginas ------------------------- */

  const switchPage = async (id) => {
    if (id === pageId) return;
    if (dirty.current && !(await savePage())) return;
    const target = pages.find((p) => p.id === id);
    setPageId(id);
    setSelectedId(null);
    setHist(createHistory(target?.content || []));
    dirty.current = false;
  };

  const addPage = async () => {
    const name = window.prompt('Nombre de la nueva página:', 'Servicios');
    if (!name) return;
    try {
      const r = await api('/api/pos/sites/' + siteId + '/pages', {
        method: 'POST', body: JSON.stringify({ name })
      });
      const data = await api('/api/pos/sites/' + siteId);
      setPages(data.pages || []);
      await switchPage(r.id);
      notify('Página creada en ' + r.path, 'ok');
    } catch (e) { notify(e.message, 'error'); }
  };

  const deletePage = async () => {
    if (!page || page.is_home) return;
    if (!window.confirm('¿Eliminar la página "' + page.name + '"? No se puede deshacer.')) return;
    try {
      await api('/api/pos/sites/' + siteId + '/pages/' + page.id, { method: 'DELETE' });
      const rest = pages.filter((p) => p.id !== page.id);
      setPages(rest);
      const home = rest.find((p) => p.is_home) || rest[0];
      setPageId(home ? home.id : null);
      setHist(createHistory(home?.content || []));
      notify('Página eliminada', 'ok');
    } catch (e) { notify(e.message, 'error'); }
  };

  /* ------------------------- tema, publicar, portabilidad ------------------------- */

  const saveTheme = async (nextTheme) => {
    setTheme(nextTheme);
    try {
      const result = await api('/api/pos/sites/' + siteId, { method: 'PUT', body: JSON.stringify({ theme: nextTheme }) });
      const pending = approvalNotice(result);
      if (pending) notify(pending, 'info');
    } catch (e) { notify('El tema no se guardó: ' + e.message, 'error'); }
  };

  const saveSeo = async (seo) => {
    setPages((prev) => prev.map((p) => (p.id === pageId ? Object.assign({}, p, { seo }) : p)));
    try {
      const result = await api('/api/pos/sites/' + siteId + '/pages/' + pageId, { method: 'PUT', body: JSON.stringify({ seo }) });
      const pending = approvalNotice(result);
      if (pending) notify(pending, 'info');
    } catch (e) { notify(e.message, 'error'); }
  };

  const publish = async () => {
    if (dirty.current) {
      const saved = await savePage();
      if (!saved) return;
    }
    try {
      const r = await api('/api/pos/sites/' + siteId + '/publish', { method: 'POST' });
      const pending = approvalNotice(r, 'Tu solicitud de publicación quedó pendiente de aprobación de MARKETOS. Todavía no está publicada.');
      if (pending) {
        notify(pending, 'info');
        return;
      }
      setSite((s) => Object.assign({}, s, { status: 'published' }));
      setPages((prev) => prev.map((p) => Object.assign({}, p, { hasUnpublishedChanges: false })));
      notify('Publicado en ' + (r.url || ('/p/' + (site?.slug || 'sitio'))), 'ok');
    } catch (e) { notify(e.message, 'error'); }
  };

  const exportSite = async () => {
    if (dirty.current && !(await savePage())) return;
    try {
      const data = await api('/api/pos/sites/' + siteId + '/export');
      downloadJson(data, (site?.slug || 'sitio') + '.marketos.json');
      notify('Archivo descargado. Impórtalo en cualquier otra cuenta.', 'ok');
    } catch (e) { notify(e.message, 'error'); }
  };

  const exportPage = () => {
    if (!page) return;
    downloadJson({
      format: 'marketos.export', version: 1, kind: 'page',
      exportedAt: new Date().toISOString(),
      payload: { name: page.name, path: page.path, seo: page.seo, theme, content: tree }
    }, (page.name || 'pagina').toLowerCase().replace(/\W+/g, '-') + '.page.json');
  };

  const importIntoPage = async (file) => {
    try {
      const data = await readJsonFile(file);
      if (data.kind === 'section' && data.payload?.node) {
        commit(tree.concat([data.payload.node]));
        notify('Sección importada al final de la página', 'ok');
      } else if (data.kind === 'page' && data.payload?.content) {
        if (!window.confirm('Esto reemplaza el contenido de la página actual. ¿Continuar?')) return;
        commit(data.payload.content);
        notify('Página importada', 'ok');
      } else {
        notify('Ese archivo es de tipo "' + (data.kind || '?') + '". Aquí solo entra una página o una sección.', 'error');
      }
    } catch (e) { notify(e.message, 'error'); }
  };

  const applyTemplate = async (tpl) => {
    try {
      const full = await api('/api/pos/site-templates/' + tpl.id);
      const env = full.payload;
      if (env.kind === 'section') {
        commit(tree.concat([env.payload.node]));
        notify('Sección agregada', 'ok');
      } else if (env.kind === 'page') {
        commit((tree || []).concat(env.payload.content || []));
        notify('Contenido agregado al final', 'ok');
      } else if (env.kind === 'site') {
        const first = env.payload.pages?.[0];
        if (first) {
          commit((tree || []).concat(first.content || []));
          notify('Se agregó el contenido de la primera página de la plantilla', 'ok');
        }
      }
    } catch (e) { notify(e.message, 'error'); }
  };

  /* ------------------------- render ------------------------- */

  if (loading) return <div className="bld-loading">Cargando el constructor…</div>;
  if (!site) return <div className="bld-loading">No se pudo cargar el sitio.</div>;

  const breadcrumb = selectedId ? (findPath(tree, selectedId) || []) : [];
  const nodeCount = countNodes(tree);

  return (
    <div className="bld">
      {/* ---------- barra superior ---------- */}
      <header className="bld-top">
        <div className="bld-top-left">
          <button type="button" className="bld-back" onClick={async () => {
            if (dirty.current && !(await savePage())) return;
            onExit && onExit();
          }}>← Mis páginas</button>
          <strong className="bld-title">{site.name}</strong>
          <span className={'bld-chip ' + (site.status === 'published' ? 'ok' : '')}>
            {site.status === 'published' ? 'Publicado' : 'Borrador'}
          </span>
          <select className="bld-pagesel" value={pageId || ''} onChange={(e) => switchPage(Number(e.target.value))}>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.is_home ? '🏠 ' : ''}{p.name}{p.hasUnpublishedChanges ? ' •' : ''}
              </option>
            ))}
          </select>
          <button type="button" className="bld-icon" title="Nueva página" onClick={addPage}>+</button>
          {page && !page.is_home && (
            <button type="button" className="bld-icon" title="Eliminar página" onClick={deletePage}>🗑</button>
          )}
        </div>

        <div className="bld-top-center">
          <div className="bld-bp">
            {[['desktop', '🖥', 'Escritorio'], ['tablet', '▭', 'Tablet'], ['mobile', '▯', 'Móvil']].map(([k, icon, label]) => (
              <button type="button" key={k} className={breakpoint === k ? 'on' : ''}
                onClick={() => setBreakpoint(k)} title={label}>{icon}</button>
            ))}
          </div>
          <button type="button" className="bld-icon" title="Deshacer (Ctrl+Z)"
            disabled={!hist.past.length}
            onClick={() => { setHist((h) => undo(h)); dirty.current = true; }}>↶</button>
          <button type="button" className="bld-icon" title="Rehacer (Ctrl+Y)"
            disabled={!hist.future.length}
            onClick={() => { setHist((h) => redo(h)); dirty.current = true; }}>↷</button>
          <button type="button" className={'bld-icon' + (showPreview ? ' on' : '')} title="Vista previa"
            onClick={() => { setShowPreview(!showPreview); setSelectedId(null); }}>👁</button>
        </div>

        <div className="bld-top-right">
          <span className={'bld-save bld-save-' + saving}>
            {saving === 'saving' ? 'Guardando…' :
              saving === 'saved' ? 'Guardado' :
                saving === 'pending' ? 'Pendiente de aprobación' :
                saving === 'error' ? 'Error al guardar' :
                  dirty.current ? 'Sin guardar' : ''}
          </span>
          <div className="bld-menu">
            <button type="button" className="bld-btn ghost">Archivo ▾</button>
            <div className="bld-menu-pop">
              <button type="button" onClick={savePage}>Guardar ahora (Ctrl+S)</button>
              <button type="button" onClick={() => setShowSeo(true)}>SEO de esta página…</button>
              <hr />
              <button type="button" onClick={exportSite}>Exportar sitio completo</button>
              <button type="button" onClick={exportPage}>Exportar esta página</button>
              <button type="button" onClick={() => importInput.current?.click()}>Importar página o sección…</button>
              <hr />
              <a href={API_URL + '/p/' + site.slug} target="_blank" rel="noopener noreferrer">Ver página pública ↗</a>
            </div>
          </div>
          <button type="button" className="bld-btn primary" onClick={publish}>Publicar</button>
        </div>
      </header>

      {status.text ? <div className={'bld-status ' + status.kind}>{status.text}</div> : null}

      <input ref={importInput} type="file" accept="application/json,.json" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) importIntoPage(f); e.target.value = ''; }} />

      <div className="bld-main">
        {/* ---------- biblioteca ---------- */}
        {!showPreview && (
          <aside className="bld-left">
            <div className="bld-left-tabs">
              {[['sections', 'Secciones'], ['blocks', 'Bloques'], ['layouts', 'Filas'], ['templates', 'Plantillas']].map(([k, label]) => (
                <button type="button" key={k} className={leftTab === k ? 'on' : ''} onClick={() => setLeftTab(k)}>{label}</button>
              ))}
            </div>
            <div className="bld-left-body">
              {leftTab === 'sections' && SECTION_GROUPS.map((group) => (
                <div key={group}>
                  <div className="bld-group-title">{group}</div>
                  {SECTION_PRESETS.filter((s) => s.group === group).map((s) => (
                    <div key={s.name} className="bld-lib-item" draggable
                      onDragStart={startDragNew('section', s.make)}
                      onDragEnd={() => setDragState(null)}
                      onDoubleClick={() => { const n = s.make(); commit(tree.concat([n])); setSelectedId(n.id); }}
                      title="Arrastra al lienzo o haz doble clic">
                      <span className="bld-lib-ico">▤</span>{s.name}
                    </div>
                  ))}
                </div>
              ))}

              {leftTab === 'blocks' && ELEMENT_GROUPS.map((group) => (
                <div key={group}>
                  <div className="bld-group-title">{group}</div>
                  <div className="bld-lib-grid">
                    {ELEMENT_LIBRARY.filter((e) => e.group === group).map((e) => (
                      <div key={e.type} className="bld-lib-cell" draggable
                        onDragStart={startDragNew(e.type, () => makeElement(e.type))}
                        onDragEnd={() => setDragState(null)}
                        title={'Arrastra ' + e.label + ' a una columna'}>
                        <b>{e.icon}</b><span>{e.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {leftTab === 'layouts' && (
                <div>
                  <div className="bld-group-title">Estructura de columnas</div>
                  <p className="bld-hint">Arrastra dentro de una sección.</p>
                  {LAYOUT_PRESETS.map((l) => (
                    <div key={l.label} className="bld-lib-item" draggable
                      onDragStart={startDragNew('row', () => makeRow(l.widths))}
                      onDragEnd={() => setDragState(null)}>
                      <span className="bld-cols">
                        {l.widths.map((w, i) => <i key={i} style={{ flexBasis: w + '%' }} />)}
                      </span>
                      {l.label}
                    </div>
                  ))}
                </div>
              )}

              {leftTab === 'templates' && (
                <div>
                  <div className="bld-group-title">Guardadas en esta cuenta</div>
                  {templates.filter((t) => !t.isSystem).length === 0 && (
                    <p className="bld-hint">
                      Aún no guardas ninguna. Selecciona una sección del lienzo y usa el botón ★
                      para reutilizarla en cualquier sitio.
                    </p>
                  )}
                  {templates.filter((t) => !t.isSystem).map((t) => (
                    <div key={t.id} className="bld-lib-item" onClick={() => applyTemplate(t)} title="Agregar al final">
                      <span className="bld-lib-ico">★</span>{t.name}
                      <em>{t.scope}</em>
                    </div>
                  ))}
                  <div className="bld-group-title">Del sistema</div>
                  {templates.filter((t) => t.isSystem).map((t) => (
                    <div key={t.id} className="bld-lib-item" onClick={() => applyTemplate(t)}>
                      <span className="bld-lib-ico">◈</span>{t.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bld-left-foot">{nodeCount} bloques en esta página</div>
          </aside>
        )}

        {/* ---------- lienzo ---------- */}
        <section className="bld-center">
          {!showPreview && breadcrumb.length > 0 && (
            <div className="bld-breadcrumb">
              <button type="button" onClick={() => setSelectedId(null)}>Página</button>
              {breadcrumb.map((n) => (
                <React.Fragment key={n.id}>
                  <span>›</span>
                  <button type="button" className={n.id === selectedId ? 'on' : ''} onClick={() => setSelectedId(n.id)}>
                    {n.type === 'section' ? 'Sección' : n.type === 'row' ? 'Fila' : n.type === 'column' ? 'Columna' : n.type}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
          <Canvas
            tree={tree}
            theme={theme}
            cssVars={cssVars}
            breakpoint={breakpoint}
            selectedId={showPreview ? null : selectedId}
            onSelect={showPreview ? () => {} : setSelectedId}
            actions={actions}
            drag={drag}
            onDragState={setDragState}
            onDrop={handleDrop}
            siteSlug={site.slug}
          />
        </section>

        {/* ---------- propiedades ---------- */}
        {!showPreview && (
          <aside className="bld-right">
            <StylePanel
              node={selected}
              breakpoint={breakpoint}
              theme={theme}
              calendars={calendars}
              setProps={actions.setProps}
              setStyle={actions.setStyle}
              setAdvanced={actions.setAdvanced}
              onTheme={saveTheme}
            />
          </aside>
        )}
      </div>

      {showSeo && page && (
        <SeoModal page={page} onClose={() => setShowSeo(false)} onSave={saveSeo} siteSlug={site.slug} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * SEO
 * ------------------------------------------------------------------ */

function SeoModal({ page, onClose, onSave, siteSlug }) {
  const [seo, setSeo] = useState(page.seo || {});
  const title = seo.title || page.name || '';
  const desc = seo.description || '';

  return (
    <div className="bld-modal" onClick={onClose}>
      <div className="bld-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>SEO y vista previa al compartir</h3>
        <label>Título</label>
        <input value={seo.title || ''} onChange={(e) => setSeo({ ...seo, title: e.target.value })}
          placeholder={page.name} maxLength={70} />
        <small>{title.length}/70 — más largo de eso, Google lo corta.</small>

        <label>Descripción</label>
        <textarea rows={3} value={seo.description || ''} maxLength={160}
          onChange={(e) => setSeo({ ...seo, description: e.target.value })} />
        <small>{desc.length}/160</small>

        <label>Imagen al compartir (URL)</label>
        <input value={seo.ogImage || ''} onChange={(e) => setSeo({ ...seo, ogImage: e.target.value })}
          placeholder="https://…" />
        <small>Es la que aparece cuando pegas el link en WhatsApp.</small>

        <label className="bld-chk">
          <input type="checkbox" checked={!!seo.noindex}
            onChange={(e) => setSeo({ ...seo, noindex: e.target.checked })} />
          Ocultar de los buscadores (útil en páginas de "gracias")
        </label>

        <div className="bld-og">
          <div className="bld-og-img" style={{ backgroundImage: seo.ogImage ? 'url(' + seo.ogImage + ')' : 'none' }} />
          <div className="bld-og-txt">
            <strong>{title || 'Título de la página'}</strong>
            <span>{desc || 'Así se verá al compartirlo por WhatsApp.'}</span>
            <em>/p/{siteSlug}{page.path === '/' ? '' : page.path}</em>
          </div>
        </div>

        <div className="bld-modal-actions">
          <button type="button" onClick={onClose}>Cancelar</button>
          <button type="button" className="primary" onClick={() => { onSave(seo); onClose(); }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}
